import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
    AuctionCatalogsService,
    AuctionDetail,
    AuctionSummary,
    GetAuctionSummary200Response,
} from '@viewbid/ts-angular-client';
import { TranslateService } from '@ngx-translate/core';
import { VbTotalRow, money } from '@viewbid/ui';
import { selectIsAdmin } from 'src/app/viewbid/state/app-selectors';
import { AppState } from 'src/app/viewbid/state/app-state';

/**
 * Admin-only "Auction summary" button + totals-ledger dialog. Extracted from
 * the retired vb-info-card sidebar so the lot list title row and the
 * watchlist rail share one implementation. Renders nothing for non-admin
 * users or while no auction is loaded.
 */
@Component({
    selector: 'vb-auction-summary',
    templateUrl: './auction-summary.component.html',
    styleUrls: ['./auction-summary.component.scss'],
})
export class AuctionSummaryComponent {
    /** Absent on surfaces without a single parent auction (e.g. watchlist). */
    @Input() auction?: AuctionDetail;

    @Input() fullWidth: boolean = false;

    public readonly isAdmin$: Observable<boolean> =
        this.store.select(selectIsAdmin);
    public summaryOpen: boolean = false;
    public isLoadingSummary: boolean = false;
    public summaryRows: VbTotalRow[] = [];
    public summaryError: string = '';

    constructor(
        private store: Store<AppState>,
        private auctionCatalogsService: AuctionCatalogsService,
        private translate: TranslateService,
    ) {}

    public get summaryEyebrow(): string {
        return this.auction?.name ?? '';
    }

    public openSummary(): void {
        this.summaryOpen = true;
        this.summaryError = '';
        this.summaryRows = [];
        this.isLoadingSummary = true;
        this.fetchAuctionSummary();
    }

    private fetchAuctionSummary(): void {
        if (!this.auction) {
            return;
        }
        this.auctionCatalogsService
            .getAuctionSummary(this.auction.id)
            .subscribe({
                next: ({ data }: GetAuctionSummary200Response) => {
                    this.prepareSummary(data);
                },
                error: () => {
                    this.isLoadingSummary = false;
                    this.summaryError = this.translate.instant(
                        'AuctionsPage.getAuctionSummeryError',
                    );
                },
            });
    }

    private prepareSummary(data: AuctionSummary): void {
        this.isLoadingSummary = false;
        this.summaryRows = [
            { label: '# of lots', value: String(data.numLots ?? 0) },
            {
                label: '# of lots with bids',
                value: String(data.numLotsWithBids ?? 0),
            },
            {
                label: '# of lots below reserve',
                value: String(data.numLotsBelowReserve ?? 0),
            },
            { label: '# of bids', value: String(data.numBids ?? 0) },
            { label: '# of watchers', value: String(data.numWatchers ?? 0) },
            {
                label: 'Current total of lots with bids (reserve met)',
                value: money(data.totalOfBidsWithReserveMet ?? 0),
            },
            {
                label: 'Total of all maximum bids',
                value: money(data.maxBidsTotal ?? 0),
            },
            {
                label: 'Current total of lots below reserve',
                value: money(data.totalOfLotsBelowReserve ?? 0),
            },
            {
                label: 'Participating bidders',
                value: String(data.numBidders ?? 0),
            },
            {
                label: 'Winning bidders',
                value: String(data.numWinningBidders ?? 0),
            },
        ];
    }
}
